import { useState, useCallback, useMemo } from 'react';
import { Box, Typography, Button, CircularProgress, Alert, Divider, Paper, Chip, IconButton } from '@mui/material';
import { CheckCircleOutlined, HomeOutlined, ArrowBackIosNewOutlined } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { globalNotifyError, globalNotifySuccess } from 'views/utilities/AlertToast';
import {
    getCities,
    getDistricts,
    getReportingManagers,
    getStates,
    storeEmployee,
    fileUploadApi,
    getDesignations,
    fetchBank
} from 'utils/API/Mis Apis/HrPortelApis';
// import { getRefreshTokenApi } from 'utils/API/RelationManagerAPIs/RelationManagerAPiCalls';
import { handleCatchError } from 'utils/CatchHandler';
import { useEffect } from 'react';
import { INITIAL_FORM, VALIDATORS } from './utils/addEmployeeConstants';
/* eslint-disable */
// Form Sections
import PersonalDetailsSection from './components/form-sections/PersonalDetailsSection';
import LocationSection from './components/form-sections/LocationSection';
import RoleAndLeadSection from './components/form-sections/RoleAndLeadSection';
import DocumentsSection from './components/form-sections/DocumentsSection';
import EmploymentDetailsSection from './components/form-sections/EmploymentDetailsSection';

// ─── Main Component ────────────────────────────────────────────────────────────
const AddSalesEmployee = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const editEmployeeData = location.state?.editEmployeeData;
    const isEditMode = !!editEmployeeData;
    const [form, setForm] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [state, setStates] = useState([]);
    const [district, setDistricts] = useState([]);
    const [cities, setCities] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [banks, setBanks] = useState([]);
    const [uploadingFields, setUploadingFields] = useState({});
    // ── Dynamic API States ───────────────────────────────────────────────────
    const [l1Options, setL1Options] = useState([]);
    const [showL1, setShowL1] = useState(false);
    const [expectedL1RoleLabel, setExpectedL1RoleLabel] = useState('Select Reporting Manager');
    const [isL1Loading, setIsL1Loading] = useState(false);

    // ── Generic field change ─────────────────────────────────────────────────
    const handleChange = useCallback(
        (field) => async (e) => {
            let value = e.target.value;

            if (field === 'email') value = value.replace(/\s/g, '');
            if (field === 'phone') {
                if (!/^\d*$/.test(value) || value.length > 10) return;
            }
            if (field === 'pancard') value = value.toUpperCase().slice(0, 10);
            if (field === 'aadharNumber') {
                if (!/^\d*$/.test(value) || value.length > 12) return;
            }
            if (field === 'name' && value !== '' && !/^[A-Za-z\s]*$/.test(value)) return;

            // Cascade resets
            let extra = {};
            if (field === 'state') {
                extra = { district: '', city: '', customCity: '' };
                FetchDistricts(value);
            }
            if (field === 'district') {
                extra = { city: '', customCity: '' };
                FetchCity(value);
            }
            if (field === 'role') {
                extra = { l1Lead: '' };

                // Fetch Reporting Managers
                if (value) {
                    setIsL1Loading(true);
                    let apiRole = value;
                    if (apiRole == 'frontline') {
                        apiRole = 'FL';
                    }
                    try {
                        const response = await getReportingManagers({ role: apiRole });
                        if (response?.data?.success) {
                            const { requires_reporting_manager, expected_reporting_role_code, reporting_managers } = response.data.data;
                            setShowL1(requires_reporting_manager);
                            // setExpectedL1RoleLabel(`Select ${expected_reporting_role_code} (L1)`);

                            // Map to expected format ({id, name})
                            setL1Options(
                                (reporting_managers || []).map((rm) => ({
                                    id: rm.id.toString(), // Ensuring id is string for Select components
                                    name: `${rm.name} (${rm.role_code}) - ${rm.employee_id || rm.email}` // Provide more context in name
                                }))
                            );
                        } else {
                            setShowL1(false);
                            setL1Options([]);
                        }
                    } catch (err) {
                        console.error('Error fetching reporting managers:', err);
                        setShowL1(false);
                        setL1Options([]);
                        globalNotifyError('Failed to fetch reporting managers.');
                    } finally {
                        setIsL1Loading(false);
                    }
                } else {
                    setShowL1(false);
                    setL1Options([]);
                }
            }

            if (field === 'dateOfOffer') {
                if (value && form.dateOfJoining && value > form.dateOfJoining) {
                    extra.dateOfJoining = '';
                    extra.effectiveDate = '';
                }
            }
            if (field === 'dateOfJoining') {
                if (value && form.effectiveDate && value > form.effectiveDate) {
                    extra.effectiveDate = '';
                }
            }

            setForm((prev) => ({ ...prev, [field]: value, ...extra }));

            if (touched[field] && VALIDATORS[field]) {
                setErrors((prev) => ({ ...prev, [field]: VALIDATORS[field](value) }));
            }
        },
        [form, touched]
    );

    // ── Blur ─────────────────────────────────────────────────────────────────
    const handleBlur = useCallback(
        (field) => () => {
            setTouched((prev) => ({ ...prev, [field]: true }));
            if (VALIDATORS[field]) {
                setErrors((prev) => ({ ...prev, [field]: VALIDATORS[field](form[field] || '') }));
            }
        },
        [form, VALIDATORS]
    );

    // ── File upload ──────────────────────────────────────────────────────────
    const handleFileChange = useCallback(
        (field, previewField, nameField) => async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            // Type validation
            if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
                globalNotifyError('Please upload an image or PDF file');
                return;
            }

            // Size validation (5MB = 5 * 1024 * 1024 bytes)
            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
                globalNotifyError('File size should not exceed 5MB');
                return;
            }

            const reader = new FileReader();
            reader.onload = async () => {
                const base64Data = reader.result;

                setUploadingFields((prev) => ({ ...prev, [field]: true }));
                try {
                    const response = await fileUploadApi({ file: base64Data });
                    if (response?.data?.success) {
                        const uploadedPath = response.data.file_path;
                        setForm((prev) => ({
                            ...prev,
                            [field]: uploadedPath,
                            [previewField]: base64Data, // Keep local preview for speed
                            [nameField]: file.name
                        }));
                        globalNotifySuccess(`${field.charAt(0).toUpperCase() + field.slice(1)} uploaded successfully`);
                    } else {
                        throw new Error(response?.data?.message || 'Upload failed');
                    }
                } catch (error) {
                    console.error('File upload error:', error);
                    globalNotifyError(`Failed to upload ${field}. Please try again.`);
                } finally {
                    setUploadingFields((prev) => ({ ...prev, [field]: false }));
                }
            };
            reader.readAsDataURL(file);
        },
        []
    );

    // ── Reset ────────────────────────────────────────────────────────────────
    const handleReset = useCallback(() => {
        setForm(INITIAL_FORM);
        setErrors({});
        setTouched({});
        setSubmitStatus(null);
        // eslint-disable-next-line
    }, []);

    // ── Validate all ─────────────────────────────────────────────────────────
    const validate = useCallback(() => {
        const newErrors = {};
        const allTouched = {};
        let isValid = true;

        Object.keys(VALIDATORS).forEach((field) => {
            allTouched[field] = true;
            const err = VALIDATORS[field](form[field] || '');
            newErrors[field] = err;
            if (err) isValid = false;
        });

        if (!form.photograph) {
            isValid = false;
            globalNotifyError('Profile photo is required.');
        }
        if (!form.aadhar) {
            isValid = false;
            globalNotifyError('Aadhar is required.');
        }
        if (!form.poaImage) {
            isValid = false;
            globalNotifyError('PAN Document is required.');
        }
        if (!form.bankDetails) {
            isValid = false;
            globalNotifyError('Bank account details are required.');
        }
        if (form.city === 'other' && !form.customCity?.trim()) {
            isValid = false;
            newErrors.customCity = 'Please enter city name';
            globalNotifyError('Please enter the custom city name.');
        }
        if (showL1 && !form.l1Lead) {
            isValid = false;
            newErrors.l1Lead = 'Please select an L1 lead';
        }

        // Date Validation: Offer <= Joining <= Effective
        if (form.dateOfOffer && form.dateOfJoining) {
            if (new Date(form.dateOfJoining) < new Date(form.dateOfOffer)) {
                isValid = false;
                globalNotifyError('Joining date must be on or after offer date.');
            }
        }
        if (form.dateOfJoining && form.effectiveDate) {
            if (new Date(form.effectiveDate) < new Date(form.dateOfJoining)) {
                isValid = false;
                globalNotifyError('Effective employment date must be on or after joining date.');
            }
        }

        setTouched(allTouched);
        setErrors(newErrors);
        return isValid;
    }, [form, showL1]);

    // ── Submit ───────────────────────────────────────────────────────────────
    const handleSubmit = useCallback(async () => {
        if (isSubmitting) return;
        if (!validate()) return;

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const payload = {
                name: form.name.trim(),
                email: form.email,
                phone: form.phone,
                pan: form.pancard,
                employee_id: editEmployeeData?.id,
                rm_employee_id: form.employeeId,
                aadhar_number: form.aadharNumber,
                aadhaar_number: form.aadharNumber,
                bank_account_number: form.bankAccountNumber,
                bank_ifsc_code: form.bankIfscCode,
                bank_name: form.bankName,
                id: form.id || null, // Include id for updates
                state: form.state,
                district: form.district,
                city: form.city === 'other' ? form.customCity.trim() : form.city,
                role: form.role === 'frontline' ? 'FL' : form.role,
                reporting_manager: form.l1Lead || null,
                date_of_offer: form.dateOfOffer || null,
                date_of_joining: form.dateOfJoining || null,
                zone: form.zone,
                designation: form.designation,
                bgv_status: form.bgvStatus,
                bgv_remarks: form.bgvRemark || null,
                bgv_document: form.bgvDocument || null,
                training_status: form.trainingStatus,
                effective_employment_date: form.effectiveDate || null,
                profile: form.photograph,
                poa: form.poaImage || null,
                offer_letter: form.offerLetter || null,
                employment_agreement: form.empAgreement || null,
                nda: form.nda || null,
                proforma: form.proforma || null,
                aadhar: form.aadhar || null,
                bank_account_document: form.bankDetails || null,
                educational_certificate: form.eduCertificate || null,
                experience_letter: form.expLetter || null,
                salary_slip: form.salarySlip || null,
                cv: form.cv || null,
                cif: form.cif || null
            };
            const response = await storeEmployee(payload);
            if (response?.data?.success) {
                globalNotifySuccess(isEditMode ? 'Sales Employee updated successfully!' : 'Sales Employee added successfully!');
                setSubmitStatus('success');
                handleReset();
            } else {
                throw new Error('API response was not successful');
            }
        } catch (err) {
            console.error('Submission error:', err);
            globalNotifyError('Failed to add employee. Please try again.');
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
        // eslint-disable-next-line
    }, [form, isSubmitting, validate, handleReset]);

    const fetchStates = async () => {
        try {
            const Response = await getStates();
            const data = Response?.data?.dmt_states;
            const formattedStates = (data || []).map((s) => ({
                id: s.id.toString(), // Store internal ID as the select value
                name: s.state,
                statecode: s.statecode
            }));
            setStates(formattedStates);
        } catch (error) {
            console.log(error.message);
            handleCatchError(error);
        }
    };
    const FetchDistricts = async (stateId) => {
        try {
            const Response = await getDistricts({ id: stateId }); // Use id as param name
            const data = Response?.data?.district;
            const formattedDistrict = (data || []).map((s) => ({
                id: s.id.toString(),
                name: s.district
            }));
            setDistricts(formattedDistrict);
        } catch (error) {
            console.log(error.message);
            handleCatchError(error);
        }
    };
    const FetchCity = async (districtId) => {
        try {
            const Response = await getCities({ id: districtId }); // Use id as param name
            const data = Response?.data?.city;
            const formattedCities = (data || []).map((s, index) => ({
                id: `${s.city}_${s.tier_val || index}`, // Ensure unique ID for selection
                name: s.city
            }));
            setCities(formattedCities);
        } catch (error) {
            console.log(error.message);
            handleCatchError(error);
        }
    };
    const fetchDesignations = async () => {
        try {
            const response = await getDesignations();
            if (response?.data?.success) {
                setDesignations(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching designations:', error);
        }
    };
    const fetchBanks = async () => {
        try {
            const response = await fetchBank();
            if (response?.data?.success) {
                setBanks(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching banks:', error);
        }
    };
    // const refreshUserToken = async () => {
    //     const role = localStorage.getItem('role');
    //     const subrole = localStorage.getItem('subrole');

    //     if (role === 'admin' && subrole === 'HR') {
    //         try {
    //             const response = await getRefreshTokenApi();

    //             if (response?.status === 200) {
    //                 const newToken = response.data?.refresh_token;
    //                 if (newToken) {
    //                     localStorage.setItem('token', newToken);
    //                 }
    //             }
    //         } catch (error) {
    //             console.error('Token refresh error:', error);
    //         }
    //     }
    // };
    useEffect(() => {
        fetchStates();
        fetchDesignations();
        fetchBanks();
        // refreshUserToken();
    }, []);

    // ── Edit Mode Population ───────────────────────────────────────────────
    useEffect(() => {
        if (editEmployeeData) {
            // Helper to get value from array or string
            const getVal = (v) => (Array.isArray(v) ? v[0] : v);

            const stateId = getVal(editEmployeeData.state);
            const districtId = getVal(editEmployeeData.district);

            setForm({
                ...INITIAL_FORM,
                id: editEmployeeData.id,
                name: editEmployeeData.name || '',
                employeeId: editEmployeeData.employee_id || editEmployeeData.rm_employee_id || '',
                email: editEmployeeData.email || '',
                phone: editEmployeeData.phone || '',
                pancard: editEmployeeData.pan || '',
                aadharNumber: editEmployeeData.aadhaar_number || editEmployeeData.aadhar_number || '',
                bankAccountNumber: editEmployeeData.bank_account_number || '',
                bankIfscCode: editEmployeeData.bank_ifsc_code || '',
                bankName: editEmployeeData.bank_name || '',
                state: stateId || '',
                district: districtId || '',
                city: getVal(editEmployeeData.city) || '',
                customCity: '',
                role: editEmployeeData.role === 'FL' ? 'frontline' : editEmployeeData.role || '',
                l1Lead: editEmployeeData.reporting_manager?.toString() || '',
                dateOfOffer: editEmployeeData.date_of_offer || '',
                date_of_joining: editEmployeeData.date_of_joining || '', // Corrected field name
                dateOfJoining: editEmployeeData.date_of_joining || '', // Also mapping to dateOfJoining for UI
                zone: editEmployeeData.zone || '',
                designation: editEmployeeData.designation_id || editEmployeeData.designation || '',
                bgvStatus: editEmployeeData.bgv_status || '',
                bgvRemark: editEmployeeData.bgv_remarks || '',
                bgvDocument: editEmployeeData.bgv_document || null,
                bgvDocumentPreview: editEmployeeData.bgv_document || null,
                trainingStatus: editEmployeeData.training_status || '',
                effectiveDate: editEmployeeData.effective_employment_date || '',
                photograph: editEmployeeData.profile || null,
                poaImage: editEmployeeData.poa || null,
                photographPreview: editEmployeeData.profile || null,
                poaImagePreview: editEmployeeData.poa || null,
                offerLetter: editEmployeeData.offer_letter || null,
                offerLetterPreview: editEmployeeData.offer_letter || null,
                empAgreement: editEmployeeData.employment_agreement || null,
                empAgreementPreview: editEmployeeData.employment_agreement || null,
                nda: editEmployeeData.nda || null,
                ndaPreview: editEmployeeData.nda || null,
                proforma: editEmployeeData.proforma || null,
                proformaPreview: editEmployeeData.proforma || null,
                aadhar: editEmployeeData.aadhar || null,
                aadharPreview: editEmployeeData.aadhar || null,
                bankDetails: editEmployeeData.bank_account_document || null,
                bankDetailsPreview: editEmployeeData.bank_account_document || null,
                eduCertificate: editEmployeeData.educational_certificate || null,
                eduCertificatePreview: editEmployeeData.educational_certificate || null,
                expLetter: editEmployeeData.experience_letter || null,
                expLetterPreview: editEmployeeData.experience_letter || null,
                salarySlip: editEmployeeData.salary_slip || null,
                salarySlipPreview: editEmployeeData.salary_slip || null,
                cv: editEmployeeData.cv || null,
                cvPreview: editEmployeeData.cv || null,
                cif: editEmployeeData.cif || null,
                cifPreview: editEmployeeData.cif || null
            });

            // Trigger cascaded fetches
            if (stateId) FetchDistricts(stateId);
            if (districtId) FetchCity(districtId);

            // Re-fetch reporting managers for the role
            if (editEmployeeData.role) {
                (async () => {
                    setIsL1Loading(true);
                    try {
                        const resp = await getReportingManagers({ role: editEmployeeData.role });
                        if (resp?.data?.success) {
                            const { requires_reporting_manager, expected_reporting_role_code, reporting_managers } = resp.data.data;
                            setShowL1(requires_reporting_manager);
                            // setExpectedL1RoleLabel(`Select ${expected_reporting_role_code} (L1)`);
                            setL1Options(
                                (reporting_managers || []).map((rm) => ({
                                    id: rm.id.toString(),
                                    name: `${rm.name} (${rm.role_code}) - ${rm.employee_id || rm.email}`
                                }))
                            );
                        }
                    } catch (e) {
                        console.error('Error fetching managers on edit:', e);
                    } finally {
                        setIsL1Loading(false);
                    }
                })();
            }
        }
    }, [editEmployeeData]);

    useEffect(() => {
        // If a previously saved city is custom, switch to 'other' entry
        if (cities.length > 0 && form.city && form.city !== 'other') {
            const cityExists = cities.some((c) => c.id === form.city);
            if (!cityExists) {
                setForm((prev) => ({
                    ...prev,
                    city: 'other',
                    customCity: prev.city
                }));
            }
        }
    }, [cities, form.city]);

    const isFormComplete = useMemo(() => {
        const isUploading = Object.values(uploadingFields).some(Boolean);
        return (
            !isUploading &&
            form.name &&
            form.email &&
            form.phone &&
            form.pancard &&
            form.aadharNumber &&
            form.state &&
            form.district &&
            form.city &&
            (form.city === 'other' ? !!form.customCity?.trim() : true) &&
            form.role &&
            (!showL1 || form.l1Lead) &&
            form.bgvStatus &&
            form.trainingStatus &&
            form.zone &&
            form.designation &&
            form.photograph &&
            form.aadhar &&
            // form.panImage &&
            form.bankDetails
        );
    }, [form, showL1, uploadingFields]);

    // ────────────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pb: 6, '& .MuiFormLabel-asterisk': { color: '#ef4444' } }}>
            {/* ── Page Header ── */}
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', // Vibrant Enterprise Blue
                        px: { xs: 2, md: 4 },
                        pt: 3,
                        pb: 6,
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: '10px'
                    }}
                >
                    {/* Decorative circles */}
                    {[
                        { size: 200, top: -80, right: -60, op: 0.07 },
                        { size: 120, top: 10, right: 120, op: 0.05 }
                    ].map((c, i) => (
                        <Box
                            key={i}
                            sx={{
                                position: 'absolute',
                                top: c.top,
                                right: c.right,
                                width: c.size,
                                height: c.size,
                                borderRadius: '50%',
                                bgcolor: '#fff',
                                opacity: c.op,
                                pointerEvents: 'none'
                            }}
                        />
                    ))}

                    <IconButton
                        onClick={() => navigate('/hr/dashboard')}
                        sx={{ color: '#fff', mb: 1, bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                        size="small"
                    >
                        <ArrowBackIosNewOutlined fontSize="inherit" />
                    </IconButton>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                        <Box>
                            <Typography variant="h4" fontWeight={800} sx={{ color: '#fff' }}>
                                {isEditMode ? 'Edit Sales Employee' : 'Add Sales Employee'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <HomeOutlined sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }} />
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                    / HR Dashboard / {isEditMode ? 'Edit Sales Employee' : 'Add Sales Employee'}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </motion.div>

            {/* ── Form Card ── */}
            <Box sx={{ px: { xs: 2, md: 4 }, mx: 'auto', mt: -4, position: 'relative', zIndex: 1 }}>
                {submitStatus === 'success' && (
                    <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSubmitStatus(null)}>
                        {isEditMode ? 'Employee updated successfully!' : 'Employee added successfully!'}
                    </Alert>
                )}
                {submitStatus === 'error' && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSubmitStatus(null)}>
                        Something went wrong. Please try again.
                    </Alert>
                )}

                <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                    {/* Accent bar */}
                    <Box sx={{ height: 4, background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)' }} />

                    <Box sx={{ p: { xs: 2, md: 4 } }}>
                        <PersonalDetailsSection
                            form={form}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            errors={errors}
                            touched={touched}
                            banks={banks}
                        />
                        <Divider sx={{ my: 4 }} />

                        <LocationSection
                            form={form}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            errors={errors}
                            touched={touched}
                            state={state}
                            district={district}
                            cities={cities}
                        />
                        <Divider sx={{ my: 4 }} />

                        <RoleAndLeadSection
                            form={form}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            errors={errors}
                            touched={touched}
                            showL1={showL1}
                            isL1Loading={isL1Loading}
                            l1Options={l1Options}
                            expectedL1RoleLabel={expectedL1RoleLabel}
                            isEditMode={isEditMode}
                            designations={designations}
                        />
                        <Divider sx={{ my: 4 }} />

                        <DocumentsSection form={form} handleFileChange={handleFileChange} uploadingFields={uploadingFields} />
                        <Divider sx={{ my: 4 }} />

                        <EmploymentDetailsSection
                            form={form}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            errors={errors}
                            touched={touched}
                            handleFileChange={handleFileChange}
                            uploadingFields={uploadingFields}
                        />

                        <Divider sx={{ my: 4 }} />

                        {/* ══ Action Buttons ══ */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleSubmit}
                                disabled={isSubmitting || !isFormComplete}
                                startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
                                sx={{
                                    borderRadius: 2,
                                    px: 4,
                                    py: 1.2,
                                    fontWeight: 700,
                                    background: isFormComplete ? 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)' : undefined,
                                    boxShadow: isFormComplete ? '0 4px 15px rgba(25,118,210,0.35)' : 'none',
                                    transition: 'all 0.2s',
                                    '&:hover': { boxShadow: '0 6px 20px rgba(25,118,210,0.45)', transform: 'translateY(-1px)' }
                                }}
                            >
                                {isSubmitting ? 'Submitting...' : isEditMode ? 'Update Employee' : 'Add Employee'}
                            </Button>

                            <Button
                                variant="outlined"
                                size="large"
                                onClick={handleReset}
                                disabled={isSubmitting}
                                sx={{ borderRadius: 2, px: 3, py: 1.2, fontWeight: 600 }}
                            >
                                Reset
                            </Button>

                            {isFormComplete && (
                                <Chip
                                    icon={<CheckCircleOutlined />}
                                    label="Ready to submit"
                                    color="success"
                                    variant="outlined"
                                    size="small"
                                />
                            )}
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default AddSalesEmployee;
